import React from "react";
import PageHero from "@/components/site/PageHero";
import { Reveal, PillButton } from "@/components/primitives";
import { useSeo } from "@/lib/useSeo";
import { MILESTONES, GROWTH_STATEMENT } from "@/lib/content";

export default function Growth() {
  useSeo({
    title: "Growth & Milestones",
    description:
      "The growth journey of Vardhix Technologies — from incorporation to an annualized revenue run rate of approximately ₹8 crore, based on internal management figures.",
    breadcrumb: [{ name: "Home", path: "/" }, { name: "Growth", path: "/growth" }],
  });

  return (
    <>
      <PageHero
        overline="Growth & Milestones"
        title="Built fast. Built responsibly. Built to scale."
        sub={GROWTH_STATEMENT}
      />

      <section className="relative py-16">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <div className="relative">
            {/* vertical line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-teal/60 via-white/10 to-transparent md:-translate-x-1/2" />
            <div className="space-y-10">
              {MILESTONES.map((m, i) => (
                <Reveal key={i} delay={0.03}>
                  <div className={`relative pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-10 ${i % 2 === 0 ? "" : "md:[&>*:first-child]:col-start-2"}`}>
                    {/* dot */}
                    <span className="absolute left-4 md:left-1/2 top-2 h-3 w-3 -translate-x-1/2 rounded-full bg-teal shadow-[0_0_14px_#00D4AA] ring-4 ring-navy" />
                    <div className={`${i % 2 === 0 ? "md:text-right md:pr-12" : "md:pl-12 md:col-start-2"}`}>
                      <div className="glass rounded-2xl p-6" data-testid={`milestone-${i}`}>
                        <span className="text-xs uppercase tracking-[0.18em] text-teal">{m.date}</span>
                        <h3 className="mt-2 text-lg font-heading font-semibold text-white">{m.title}</h3>
                        <p className="mt-2 text-sm text-slate-400 leading-relaxed">{m.desc}</p>
                        {m.reported && (
                          <span className="mt-3 inline-block rounded-full bg-gold/10 border border-gold/30 px-2.5 py-1 text-[10px] uppercase tracking-wider text-gold">
                            Company-reported figure
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.1}>
            <p className="mt-12 text-xs text-slate-500 text-center max-w-2xl mx-auto">
              Revenue and user numbers are company-reported figures based on internal management data
              and may be unaudited unless independently verified.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="relative py-20 border-t border-white/10">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-white">Grow with Vardhix</h2>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <PillButton to="/careers" testid="growth-careers-cta">Explore Careers</PillButton>
            <PillButton to="/contact" variant="secondary" testid="growth-contact-cta">Partner With Us</PillButton>
          </div>
        </div>
      </section>
    </>
  );
}
